import React, { useState, useMemo } from "react";
import { 
  AlertTriangle, 
  Package, 
  Bookmark, 
  TrendingDown, 
  Plus, 
  Search, 
  CheckCircle,
  Clock,
  ArrowRight,
  ShieldAlert
} from "lucide-react";
import { loteService } from "../services/loteService";

export default function AlertasSubTab({ user, inventory, reservations, findCatalogItem, onRefresh }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [creatingSku, setCreatingSku] = useState(null);

  // SKUs abaixo do mínimo ou com reservas represadas
  const alertsData = useMemo(() => {
    if (!Array.isArray(inventory)) return { minStockAlerts: [], reservationAlerts: [] };

    // 1. Agrupa estoque por SKU
    const readyQtyBySku = {};
    inventory.forEach(item => {
      if (item.status === "in_stock" && !item.isPE) {
        readyQtyBySku[item.sku] = (readyQtyBySku[item.sku] || 0) + 1;
      }
    });

    // 2. Agrupa reservas pendentes por SKU
    const resQtyBySku = {};
    if (Array.isArray(reservations)) {
      reservations.forEach(r => {
        if (r.status === "pending" || r.status === "reservado") {
          resQtyBySku[r.sku] = (resQtyBySku[r.sku] || 0) + (r.quantity || 1);
        }
      });
    }

    // 3. SKUs abaixo do estoque mínimo (Padrão 2 ou customizado)
    const minStockAlerts = [];
    const reservationAlerts = [];

    // Considera todos os SKUs no inventário
    const allSkus = Array.from(new Set(inventory.map(i => i.sku)));

    allSkus.forEach(sku => {
      const ready = readyQtyBySku[sku] || 0;
      const cat = findCatalogItem ? findCatalogItem(sku) : null;
      const minStock = Number(
        cat?.estoque_minimo_definido ?? 
        cat?.min_stock ?? 
        cat?.estoque_minimo_sugerido ?? 
        0
      );

      // Apenas gera alerta de mínimo se houver um valor mínimo explicitamente configurado (> 0)
      if (minStock > 0 && ready < minStock) {
        minStockAlerts.push({
          sku,
          name: cat?.name || "Produto sem cadastro",
          readyQty: ready,
          minStock,
          deficit: minStock - ready
        });
      }

      const reserved = resQtyBySku[sku] || 0;
      if (reserved > ready) {
        reservationAlerts.push({
          sku,
          name: cat?.name || "Produto sem cadastro",
          readyQty: ready,
          reservedQty: reserved,
          deficit: reserved - ready
        });
      }
    });

    return { minStockAlerts, reservationAlerts };
  }, [inventory, reservations, findCatalogItem]);

  // Filtragem por busca
  const filteredMinAlerts = alertsData.minStockAlerts.filter(a => 
    searchTerm === "" || 
    a.sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredResAlerts = alertsData.reservationAlerts.filter(a => 
    searchTerm === "" || 
    a.sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Ação: Criar lote de reposição no Buffer
  const handleCreateReplenishmentLote = async (alertItem) => {
    const qtyToProduce = alertItem.deficit > 0 ? alertItem.deficit : 2;
    if (!window.confirm(`Deseja criar uma solicitação de lote no buffer para ${qtyToProduce} unidade(s) do SKU ${alertItem.sku}?`)) return;

    setCreatingSku(alertItem.sku);
    try {
      const author = user?.name || "Central de Alertas";
      await loteService.requestLoteFromOficina(
        [{ sku: alertItem.sku, qtd: qtyToProduce }],
        author,
        `Reposição Automática de Estoque Mínimo (Defasagem: ${qtyToProduce} pçs)`
      );
      alert("Solicitação de lote enviada para o buffer de aprovações com sucesso!");
      if (onRefresh) onRefresh();
    } catch (e) {
      alert("Erro ao criar reposição: " + e.message);
    } finally {
      setCreatingSku(null);
    }
  };

  return (
    <div className="flex flex-col h-full w-full bg-slate-50 overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4 flex flex-wrap justify-between items-center gap-4 shrink-0 shadow-sm">
        <div>
          <h2 className="text-base font-black text-slate-800 flex items-center gap-2">
            <ShieldAlert className="text-rose-600" size={20} /> Central de Alertas e Níveis Mínimos
          </h2>
          <p className="text-xs text-slate-400">
            Monitore SKUs com estoque abaixo do limite mínimo e reservas pendentes aguardando reposição.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <span className="text-xs font-bold bg-rose-50 text-rose-700 px-3 py-1 rounded-full border border-rose-200 flex items-center gap-1.5">
            <AlertTriangle size={14} /> {alertsData.minStockAlerts.length} abaixo do mínimo
          </span>
          <span className="text-xs font-bold bg-amber-50 text-amber-700 px-3 py-1 rounded-full border border-amber-200 flex items-center gap-1.5">
            <Bookmark size={14} /> {alertsData.reservationAlerts.length} reservas sem estoque
          </span>
        </div>
      </div>

      {/* Conteúdo Principal */}
      <div className="flex-1 overflow-auto p-4 md:p-6 custom-scrollbar">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Barra de Busca */}
          <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-3 text-slate-400" />
              <input
                type="text"
                placeholder="Filtrar alertas por SKU ou nome do produto..."
                className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs outline-none focus:border-purple-500 text-slate-800"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* Grid de Seções de Alerta */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Seção 1: Estoque Crítico (Abaixo do Mínimo) */}
            <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm space-y-4">
              <div className="flex justify-between items-center border-b border-slate-100 pb-3">
                <h3 className="text-sm font-black text-slate-800 flex items-center gap-2">
                  <TrendingDown className="text-rose-600" size={18} /> Estoque Abaixo do Mínimo ({filteredMinAlerts.length})
                </h3>
              </div>

              {filteredMinAlerts.length === 0 ? (
                <div className="text-center py-8 text-slate-400 text-xs italic">
                  Nenhum SKU abaixo do limite de estoque mínimo.
                </div>
              ) : (
                <div className="space-y-3 max-h-[500px] overflow-y-auto pr-1 custom-scrollbar">
                  {filteredMinAlerts.map(item => (
                    <div key={item.sku} className="border border-slate-200 rounded-xl p-3.5 flex items-center justify-between gap-3 bg-slate-50/50 hover:bg-white transition-all">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-extrabold text-xs text-blue-600">{item.sku}</span>
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-rose-100 text-rose-800 border border-rose-200">
                            Faltam {item.deficit} pç{item.deficit !== 1 ? "s" : ""}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 truncate mt-0.5">{item.name}</p>
                        <div className="flex items-center gap-3 text-[10px] text-slate-400 font-bold mt-1">
                          <span>Pronto: {item.readyQty} pçs</span>
                          <span>Mínimo: {item.minStock} pçs</span>
                        </div>
                      </div>

                      <button
                        onClick={() => handleCreateReplenishmentLote(item)}
                        disabled={creatingSku === item.sku}
                        className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-xl text-xs font-bold shadow-sm transition-all flex items-center gap-1 shrink-0 cursor-pointer disabled:bg-purple-300"
                      >
                        <Plus size={14} /> Repor Lote
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Seção 2: Reservas Represadas sem Estoque */}
            <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm space-y-4">
              <div className="flex justify-between items-center border-b border-slate-100 pb-3">
                <h3 className="text-sm font-black text-slate-800 flex items-center gap-2">
                  <Bookmark className="text-amber-600" size={18} /> Reservas Aguardando Reposição ({filteredResAlerts.length})
                </h3>
              </div>

              {filteredResAlerts.length === 0 ? (
                <div className="text-center py-8 text-slate-400 text-xs italic">
                  Nenhuma reserva pendente aguardando peças de pronta entrega.
                </div>
              ) : (
                <div className="space-y-3 max-h-[500px] overflow-y-auto pr-1 custom-scrollbar">
                  {filteredResAlerts.map(item => (
                    <div key={item.sku} className="border border-slate-200 rounded-xl p-3.5 flex items-center justify-between gap-3 bg-slate-50/50 hover:bg-white transition-all">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-extrabold text-xs text-purple-600">{item.sku}</span>
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">
                            Defasagem: {item.deficit} pç{item.deficit !== 1 ? "s" : ""}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 truncate mt-0.5">{item.name}</p>
                        <div className="flex items-center gap-3 text-[10px] text-slate-400 font-bold mt-1">
                          <span>Pronto: {item.readyQty} pçs</span>
                          <span>Reservado: {item.reservedQty} pçs</span>
                        </div>
                      </div>

                      <button
                        onClick={() => handleCreateReplenishmentLote(item)}
                        disabled={creatingSku === item.sku}
                        className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-xl text-xs font-bold shadow-sm transition-all flex items-center gap-1 shrink-0 cursor-pointer disabled:bg-amber-300"
                      >
                        <Plus size={14} /> Solicitado Demanda
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
